import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Container } from "@/components/layout/index";
import { cn } from "@/lib/utils";
import { getAccessToken } from "@/lib/authToken";
import { getStoredSubscribedPlan } from "@/lib/subscriptionStorage";
import { PricingPlansGrid } from "@/components/pricing/PricingPlansGrid";
import { formatQuickProfileTagline } from "@/lib/prePaywallOnboardingStorage";
import { MobilePaywallSimplified } from "@/components/paywall/MobilePaywallSimplified";
import { PaywallUnlockValue } from "@/components/paywall/PaywallUnlockValue";
import { MarketingHeroHeadline } from "@/components/marketing/MarketingHeroHeadline";
import {
  MARKETING_HERO_SUBTITLE_AFTER_ROLE,
  MARKETING_HERO_SUBTITLE_FALLBACK,
  marketingHeroSubtitleParts,
} from "@/constants/marketingHeroCopy";
import { startPlanCheckout } from "@/lib/checkout";

/**
 * Pricing overlay: drawer on mobile; full marketing + grid from md up.
 */
export function PricingPaywallDialog({
  open,
  onOpenChange,
  paywallPayload = {},
  onCloseQuiet = () => {},
}) {
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );
  const [subscribedPlan, setSubscribedPlan] = useState(() =>
    typeof window !== "undefined" && getAccessToken() ? getStoredSubscribedPlan() : null
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const sync = () =>
      setSubscribedPlan(getAccessToken() ? getStoredSubscribedPlan() : null);
    sync();
    window.addEventListener("rareRoles:auth-changed", sync);
    window.addEventListener("rareroles:subscription-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("rareRoles:auth-changed", sync);
      window.removeEventListener("rareroles:subscription-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const quickTagline = formatQuickProfileTagline(paywallPayload?.quickProfile);
  const jobTitle =
    typeof paywallPayload?.jobTitle === "string" ? paywallPayload.jobTitle.trim() : "";
  const heroSub = marketingHeroSubtitleParts(jobTitle);
  const jobId =
    paywallPayload?.jobId != null ? String(paywallPayload.jobId).trim() : "";

  const startCheckout = async (planId) => {
    setCheckoutLoading(planId);
    try {
      await startPlanCheckout(planId, jobId ? { jobId } : {});
    } catch {
      setCheckoutLoading(null);
    }
  };

  const mobilePaywall = (
    <MobilePaywallSimplified
      open={open}
      payload={paywallPayload}
      checkoutLoading={checkoutLoading}
      subscribedPlan={subscribedPlan}
      onSelectPlan={startCheckout}
      onCloseQuiet={onCloseQuiet}
      onRequestClose={() => onOpenChange(false)}
    />
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          className={cn(
            "max-h-[96dvh] rounded-t-2xl border-t p-0",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            "duration-200"
          )}
          data-testid="paywall-mobile-drawer"
        >
          <DrawerTitle className="sr-only">Start your free trial</DrawerTitle>
          <DrawerDescription className="sr-only">
            Unlock full job details, apply links, and AI tools with a free trial.
          </DrawerDescription>
          {mobilePaywall}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-h-[calc(100dvh-0.25rem)] w-[calc(100vw-1rem)] max-w-7xl overflow-y-auto rounded-2xl border bg-background p-0 gap-0 sm:w-[calc(100vw-1.5rem)] xl:max-w-[min(92vw,88rem)]",
          "md:min-h-[min(74dvh,48rem)] lg:min-h-[min(76dvh,50rem)]",
          "left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 duration-200"
        )}
        overlayClassName="bg-background/80 backdrop-blur-sm z-[100]"
      >
        <DialogTitle className="sr-only">Subscribe to RareRoles</DialogTitle>
        <DialogDescription className="sr-only">
          Choose a plan for full job descriptions, requirements, company context, direct apply links, and resume and
          cover letter tools.
        </DialogDescription>

        <div className="relative overflow-hidden border-b border-border/80 bg-background">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.05] via-transparent to-highlight/[0.04]" aria-hidden />
          <div className="hero-line-grid absolute inset-0 pointer-events-none opacity-[0.75]" aria-hidden />
          <div
            className="animate-landing-blob-tr absolute -top-32 -right-32 h-[20rem] w-[20rem] rounded-full bg-primary/[0.07] blur-[80px] pointer-events-none"
            aria-hidden
          />
          <Container className="relative px-4 py-6 sm:px-8 sm:py-8 md:px-10 md:py-12 lg:py-14">
            <MarketingHeroHeadline
              as="h2"
              className="font-heading text-center text-2xl font-bold leading-[1.15] tracking-tight text-balance text-foreground sm:text-3xl md:text-4xl lg:text-[2.5rem]"
            />
            <p className="mx-auto mt-2.5 max-w-2xl text-center text-sm leading-snug text-muted-foreground sm:mt-3 sm:text-base">
              {heroSub.personalized ? (
                <>
                  Unlock{" "}
                  <span className="font-semibold text-foreground">{heroSub.role}</span>{" "}
                  {MARKETING_HERO_SUBTITLE_AFTER_ROLE}
                </>
              ) : (
                MARKETING_HERO_SUBTITLE_FALLBACK
              )}
            </p>
            {jobTitle ? (
              <p
                className="mx-auto mt-3 max-w-xl truncate text-center text-sm font-medium text-foreground/80"
                data-testid="paywall-desktop-job-context"
              >
                {jobTitle}
                <span className="text-muted-foreground"> · Company hidden until unlock</span>
              </p>
            ) : null}
            {quickTagline ? (
              <p className="mx-auto mt-3 max-w-2xl text-center text-sm font-semibold leading-snug text-primary sm:text-base">
                {quickTagline}
              </p>
            ) : null}
          </Container>
        </div>

        <div className="px-3 py-6 sm:px-6 sm:py-8 md:px-10 md:py-14 lg:py-16 md:pb-16 lg:pb-20">
          <PaywallUnlockValue
            compact
            trackImpression={open}
            jobTitle={jobTitle || undefined}
            className="mx-auto mb-6 max-w-md sm:mb-8 md:mb-10"
          />
          <PricingPlansGrid
            compact
            checkoutLoading={checkoutLoading}
            subscribedPlan={subscribedPlan}
            onSelectPlan={startCheckout}
          />
          <p className="mt-6 text-center text-xs text-muted-foreground sm:text-sm md:mt-8 lg:mt-10 animate-landing-fade-up animate-landing-fade-up-delay-4">
            Cancel anytime · No hidden fees · Secured by Stripe
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
